export type SmsSettingsData = {
  configured: boolean;
  provider: "notify.lk";
  senderId: string;
  welcomeMsg: string;
  appointmentMsg: string;
  appRescheduleMsg: string;
  appReminderMsg: string;
  appHourBeforeReminderMsg: string;
  morningReminderTime: string;
  invoiceMsg: string;
  status: boolean;
  birthdayMsg: string;
  birthdayEnabled: boolean;
  followUpMsg: string;
  followUpEnabled: boolean;
  followUpDaysAfter: number;
  holidayMsg: string;
  holidayEnabled: boolean;
  holidayDates: { name: string; date: string }[];
  oralHealthTipMsg: string;
  oralHealthTipEnabled: boolean;
  oralHealthTipDay: number;
  promoMsg: string;
  promoEnabled: boolean;
  promoMonths: number[];
  promoDay: number;
};

export type SmsMessageTypeConfig = {
  field: keyof Pick<
    SmsSettingsData,
    | "welcomeMsg"
    | "appointmentMsg"
    | "appReminderMsg"
    | "appHourBeforeReminderMsg"
    | "appRescheduleMsg"
    | "invoiceMsg"
  >;
  type: string;
  label: string;
  description: string;
  placeholders: string[];
};

export type PromotionMessageTypeConfig = {
  field: keyof Pick<
    SmsSettingsData,
    "birthdayMsg" | "followUpMsg" | "holidayMsg" | "oralHealthTipMsg" | "promoMsg"
  >;
  enabledField: keyof Pick<
    SmsSettingsData,
    | "birthdayEnabled"
    | "followUpEnabled"
    | "holidayEnabled"
    | "oralHealthTipEnabled"
    | "promoEnabled"
  >;
  type: string;
  label: string;
  description: string;
  placeholders: string[];
};

export const SMS_MESSAGE_TYPES: SmsMessageTypeConfig[] = [
  {
    field: "welcomeMsg",
    type: "welcome",
    label: "Patient welcome",
    description: "Sent when a new patient is registered",
    placeholders: ["{{patient_name}}", "{{clinic_name}}", "{{registration_number}}"],
  },
  {
    field: "appointmentMsg",
    type: "appointment",
    label: "Appointment confirmation",
    description: "Sent when an appointment is booked",
    placeholders: [
      "{{patient_name}}",
      "{{clinic_name}}",
      "{{appointment_date}}",
      "{{appointment_time}}",
    ],
  },
  {
    field: "appReminderMsg",
    type: "reminder",
    label: "Morning appointment reminder",
    description: "Sent on the appointment day at your morning reminder time",
    placeholders: [
      "{{patient_name}}",
      "{{clinic_name}}",
      "{{appointment_date}}",
      "{{appointment_time}}",
    ],
  },
  {
    field: "appHourBeforeReminderMsg",
    type: "reminder-hour",
    label: "One hour before reminder",
    description: "Sent automatically one hour before the appointment starts",
    placeholders: [
      "{{patient_name}}",
      "{{clinic_name}}",
      "{{appointment_date}}",
      "{{appointment_time}}",
    ],
  },
  {
    field: "appRescheduleMsg",
    type: "reschedule",
    label: "Appointment reschedule",
    description: "Sent when an appointment is rescheduled",
    placeholders: [
      "{{patient_name}}",
      "{{clinic_name}}",
      "{{appointment_date}}",
      "{{appointment_time}}",
    ],
  },
  {
    field: "invoiceMsg",
    type: "invoice",
    label: "Invoice notification",
    description: "Sent when an invoice link is shared with a patient",
    placeholders: ["{{patient_name}}", "{{clinic_name}}", "{{invoice_link}}"],
  },
];

export const PROMOTION_MESSAGE_TYPES: PromotionMessageTypeConfig[] = [
  {
    field: "birthdayMsg",
    enabledField: "birthdayEnabled",
    type: "birthday",
    label: "Birthday wishes",
    description:
      "Sent on patient birthdays for patients opted in when adding patients",
    placeholders: ["{{patient_name}}", "{{clinic_name}}"],
  },
  {
    field: "followUpMsg",
    enabledField: "followUpEnabled",
    type: "follow-up",
    label: "Treatment completion follow-up",
    description: "Sent after treatment is completed",
    placeholders: ["{{patient_name}}", "{{clinic_name}}", "{{treatment_name}}"],
  },
  {
    field: "holidayMsg",
    enabledField: "holidayEnabled",
    type: "holiday",
    label: "Holiday greetings",
    description: "Sent to all patients on configured holiday dates",
    placeholders: ["{{patient_name}}", "{{clinic_name}}", "{{holiday_name}}"],
  },
  {
    field: "oralHealthTipMsg",
    enabledField: "oralHealthTipEnabled",
    type: "oral-health-tip",
    label: "Oral health tips (monthly)",
    description: "Sent monthly on the configured day",
    placeholders: ["{{patient_name}}", "{{clinic_name}}", "{{health_tip}}"],
  },
  {
    field: "promoMsg",
    enabledField: "promoEnabled",
    type: "promo",
    label: "Promotional offers",
    description: "Sent during slow periods or manually to selected patients",
    placeholders: ["{{patient_name}}", "{{clinic_name}}", "{{offer_details}}"],
  },
];

export const MONTH_OPTIONS = [
  { value: 1, label: "Jan" },
  { value: 2, label: "Feb" },
  { value: 3, label: "Mar" },
  { value: 4, label: "Apr" },
  { value: 5, label: "May" },
  { value: 6, label: "Jun" },
  { value: 7, label: "Jul" },
  { value: 8, label: "Aug" },
  { value: 9, label: "Sep" },
  { value: 10, label: "Oct" },
  { value: 11, label: "Nov" },
  { value: 12, label: "Dec" },
];

export function isSmsTypeConfigured(
  settings: SmsSettingsData | null,
  field: SmsMessageTypeConfig["field"] | PromotionMessageTypeConfig["field"]
) {
  return Boolean(String(settings?.[field] || "").trim());
}

export function formatSmsTypeLabel(value: string | null | undefined) {
  if (!value) return "—";
  const match =
    SMS_MESSAGE_TYPES.find((item) => item.type === value) ||
    PROMOTION_MESSAGE_TYPES.find((item) => item.type === value);
  if (match) return match.label;
  if (value === "reminder-hour") return "One hour before reminder";
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/-/g, " ");
}
